import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';

const fmt = (n) => Number(n || 0).toLocaleString();

const CheckCircleIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

const ContractorDecisionPage = () => {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [estimate, setEstimate] = useState(null);
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const customerId = localStorage.getItem('customer_id');

    useEffect(() => {
        api.get(`projects/${projectId}`).then(r => setProject(r.data)).catch(console.error);
        api.get(`estimates/by_project/${projectId}`).then(r => setEstimate(r.data)).catch(() => {});
    }, [projectId]);

    const handleDecide = async () => {
        setLoading(true);
        setError(null);
        try {
            await api.post(`projects/${projectId}/decide_contractor`, {
                customer_id: Number(customerId),
            });
            setDone(true);
        } catch {
            setError('発注処理に失敗しました。もう一度お試しください。');
            setLoading(false);
        }
    };

    if (!project) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const contractor = project.contractor;
    const condition = project.condition;

    // 発注完了画面
    if (done) {
        return (
            <div style={{ minHeight: '100vh', background: '#f5f6f8', padding: '80px 16px 40px' }}>
                <div style={{ maxWidth: 520, margin: '0 auto', background: '#fff', borderRadius: 12, padding: '48px 40px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                    <div style={{ marginBottom: 20 }}>
                        <CheckCircleIcon />
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 12px' }}>
                        発注が確定しました
                    </h2>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0 0 8px', lineHeight: 1.7 }}>
                        <strong style={{ color: '#1a1a2e' }}>{contractor?.company_name}</strong> に発注しました。<br />
                        業者から請求書が届くまでお待ちください。
                    </p>
                    <p style={{ fontSize: '0.82rem', color: '#9ca3af', margin: '0 0 32px' }}>
                        他にやり取りしていた業者には失注の通知が送られます。
                    </p>
                    <button
                        onClick={() => navigate('/customer/progress')}
                        style={{ width: '100%', padding: '13px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', marginBottom: 10 }}
                    >
                        進行状況を確認する
                    </button>
                    <button
                        onClick={() => navigate('/customer/dashboard')}
                        style={{ width: '100%', padding: '12px', background: 'none', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 6, fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer' }}
                    >
                        マイページへ
                    </button>
                </div>
            </div>
        );
    }

    // 確認画面
    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8', padding: '60px 16px 40px' }}>
            <div style={{ maxWidth: 560, margin: '0 auto' }}>
                <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px', textAlign: 'center' }}>
                    発注の確認
                </h1>

                {/* 業者情報カード */}
                <div style={{ background: '#fff', borderRadius: 10, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16 }}>
                    <p style={{ fontSize: '0.78rem', fontWeight: 500, color: '#6b7280', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        発注先
                    </p>
                    <p style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 12px' }}>
                        {contractor?.company_name}
                    </p>

                    {condition && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                            {condition.work_types?.map(w => (
                                <span key={w} style={{ padding: '3px 10px', background: '#e6f2f1', color: '#005c53', borderRadius: 12, fontSize: '0.78rem', fontWeight: 500 }}>
                                    {w}
                                </span>
                            ))}
                            {(condition.location_prefecture || condition.location_city) && (
                                <span style={{ padding: '3px 10px', background: '#f5f6f8', color: '#6b7280', borderRadius: 12, fontSize: '0.78rem' }}>
                                    {condition.location_prefecture}{condition.location_city}
                                </span>
                            )}
                        </div>
                    )}

                    {estimate && (
                        <div style={{ paddingTop: 12, borderTop: '1px solid #f5f6f8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>見積金額</span>
                            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' }}>
                                ¥{fmt(estimate.amount)}
                            </span>
                        </div>
                    )}
                </div>

                {/* 注意事項 */}
                <div style={{ background: '#fff8e1', border: '1px solid #fbbf24', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: '0.84rem', color: '#92400e' }}>
                    決定後、他の業者とのやり取りはキャンセルされます。
                </div>

                {error && (
                    <p style={{ color: '#e53935', fontSize: '0.875rem', marginBottom: 12, textAlign: 'center' }}>{error}</p>
                )}

                {/* ボタン */}
                <div style={{ display: 'flex', gap: 10 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{ flex: 1, padding: '13px', background: '#fff', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 6, fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer' }}
                    >
                        戻る
                    </button>
                    <button
                        onClick={handleDecide}
                        disabled={loading}
                        style={{ flex: 2, padding: '13px', background: loading ? '#9ca3af' : '#005c53', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.95rem', cursor: loading ? 'default' : 'pointer', transition: 'background 0.15s' }}
                    >
                        {loading ? '処理中...' : 'この業者に決定する'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContractorDecisionPage;
